import type { CallLine, CallTab, SpeakerTrack, TimeSpan } from "./types";

export function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** Playback position, as 00:00 or 1:02:03. */
export function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");
  if (hours > 0) return `${hours}:${mm}:${ss}`;
  return `${mm}:${ss}`;
}

function formatLength(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  return formatDuration(total);
}

export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function transcriptText(lines: CallLine[]): string {
  return lines.map((line) => `${line.speaker}: ${line.sentence}`).join("\n");
}

/** Where a click on a timeline lands, or null when the bar has no width. */
export function secondsAt(
  clientX: number,
  left: number,
  width: number,
  span: number,
): number | null {
  if (width <= 0 || span <= 0) return null;
  const ratio = (clientX - left) / width;
  return Math.min(span, Math.max(0, ratio * span));
}

export function skipBy(position: number, delta: number, span: number): number {
  return Math.min(span, Math.max(0, position + delta));
}

export type SpeakerJump = { at: number; name: string };

const SPEAKER_JUMP_EPS = 0.05;

type SpeechTurn = SpeakerJump & { speakerId: string; end: number };

function speechTurns(speakers: SpeakerTrack[]): SpeechTurn[] {
  const turns: SpeechTurn[] = [];
  for (const track of speakers) {
    for (const segment of track.segments) {
      if (!(segment.end > segment.start)) continue;
      turns.push({
        speakerId: track.speaker_id,
        name: track.name,
        at: segment.start,
        end: segment.end,
      });
    }
  }
  turns.sort((a, b) => a.at - b.at || a.end - b.end);
  return turns;
}

/** The voice underway at `position`: the one that started latest, if several overlap. */
function turnAt(turns: SpeechTurn[], position: number): SpeechTurn | null {
  let current: SpeechTurn | null = null;
  for (const turn of turns) {
    if (turn.at <= position && position < turn.end && (!current || turn.at >= current.at)) {
      current = turn;
    }
  }
  return current;
}

/**
 * Who the next-speaker control should skip.
 *
 * Inside a turn, that is the person talking. In a pause between lines — the
 * usual case, because a gap stays a gap — it is whoever finished most recently.
 * Before anyone has spoken there is nobody to skip.
 */
function activeSpeakerId(turns: SpeechTurn[], position: number): string | null {
  const speaking = turnAt(turns, position);
  if (speaking) return speaking.speakerId;
  let recent: SpeechTurn | null = null;
  for (const turn of turns) {
    if (turn.end > position) continue;
    if (!recent || turn.end > recent.end || (turn.end === recent.end && turn.at > recent.at)) {
      recent = turn;
    }
  }
  return recent?.speakerId ?? null;
}

/**
 * Where to land to hear the next attendee.
 *
 * Skips the current voice, including the rest of their own lines, and lands
 * when someone else starts. With nobody else still to come, the jump is to
 * the same person's next turn.
 */
export function speakerJump(speakers: SpeakerTrack[], position: number): SpeakerJump | null {
  const turns = speechTurns(speakers);
  const currentId = activeSpeakerId(turns, position);
  let same: SpeechTurn | null = null;
  for (const turn of turns) {
    if (turn.at <= position + SPEAKER_JUMP_EPS) continue;
    if (currentId == null || turn.speakerId !== currentId) {
      return { at: turn.at, name: turn.name };
    }
    if (!same) same = turn;
  }
  return same ? { at: same.at, name: same.name } : null;
}

/** Each speaker's share of the time anyone was talking, as a whole percent. */
export function talkShares(speakers: SpeakerTrack[]): number[] {
  const lengths = speakers.map((track) =>
    track.segments.reduce((sum, segment) => sum + Math.max(0, segment.end - segment.start), 0),
  );
  const all = lengths.reduce((sum, length) => sum + length, 0);
  if (!all) return lengths.map(() => 0);
  return lengths.map((length) => Math.round((length / all) * 100));
}

/** A headed block of the brief. The page draws every block as bullets. */
export type BriefBlock = {
  heading: string;
  text: string;
};

const CORE_TOPIC_LIMIT = 5;
const CORE_TOPIC_SHARE = 0.08;

function norm(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function alreadyCovered(haystack: string, text: string): boolean {
  const needle = norm(text);
  if (!needle) return true;
  const sample = needle.length > 80 ? needle.slice(0, 80) : needle;
  return haystack.includes(sample);
}

/** Heading lines are `# What was discussed`. Anything else is not a headed brief. */
function headedBlocks(brief: string | null): BriefBlock[] | null {
  const text = brief?.trim() ?? "";
  if (!text.startsWith("# ") && !text.includes("\n# ")) return null;
  const blocks: BriefBlock[] = [];
  for (const part of text.split(/\n(?=# )/)) {
    const lines = part.trim().split("\n");
    const heading = lines[0].replace(/^#\s+/, "").trim();
    const body = lines.slice(1).join("\n").trim();
    if (heading && body) blocks.push({ heading, text: body });
  }
  return blocks.length > 0 ? blocks : null;
}

function keyPointBlock(brief: string | null): BriefBlock[] {
  const lines = (brief ?? "")
    .split("\n")
    .map((line) => line.replace(/^[-•]\s*/, "").trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  return [{ heading: "Key points", text: lines.join("\n") }];
}

/**
 * The brief, with headings. A headed write-up is shown as written. A flat
 * one is key points, then any action point the brief does not already
 * contain. The summary stays on its own tab: it is the longer account.
 */
export function briefBlocks(brief: string | null, actionPoints: string[]): BriefBlock[] {
  const blocks = headedBlocks(brief) ?? keyPointBlock(brief);
  const haystack = norm(blocks.map((block) => block.text).join("\n"));
  const missing = actionPoints
    .map((point) => point.trim())
    .filter((point) => point && !alreadyCovered(haystack, point));
  if (missing.length > 0) {
    const heading = blocks.some((block) => norm(block.heading) === "next steps")
      ? "Action points"
      : "Next steps";
    blocks.push({ heading, text: missing.join("\n") });
  }
  return blocks;
}

const BULLET_LINE = /^(?:[-•]|\*(?!\*))\s+(.*)$/;
const ORDERED_LINE = /^\d+[.)]\s+(.*)$/;

function bareItem(line: string): string {
  return line.trim().replace(ORDERED_LINE, "$1").replace(BULLET_LINE, "$1").trim();
}

/** A list the brief already split into lines, drawn as bullets. */
function listMarkdown(text: string, ordered: boolean): string {
  const items = text.split("\n").map(bareItem).filter(Boolean);
  return items.map((item, index) => (ordered ? `${index + 1}. ${item}` : `- ${item}`)).join("\n");
}

/**
 * Sentence breaks follow a word of at least three letters, so "Dr. Smith"
 * stays one sentence and "notice. She" splits.
 */
function sentences(text: string): string[] {
  const items: string[] = [];
  const breaks = /(?<=\b[A-Za-z]{3,}[.!?])\s+(?=[A-Z*])/g;
  let start = 0;
  for (const match of text.matchAll(breaks)) {
    const index = match.index ?? 0;
    const sentence = text.slice(start, index).trim();
    if (sentence) items.push(sentence);
    start = index + match[0].length;
  }
  const tail = text.slice(start).trim();
  if (tail) items.push(tail);
  return items.length > 0 ? items : [text.trim()].filter(Boolean);
}

function discussedHeading(heading: string): boolean {
  const name = norm(heading);
  return name === "what was discussed" || name === "summary";
}

/**
 * Every line under a heading becomes a bullet. A long "what was discussed"
 * paragraph keeps its opening sentence, so that section stays shorter than
 * the summary. Other sections keep each sentence. Bullets already written
 * are left as they are.
 */
function briefItems(heading: string, text: string): string[] {
  const discussed = discussedHeading(heading);
  const items: string[] = [];
  for (const row of text.replace(/\r\n/g, "\n").split("\n")) {
    const trimmed = row.trim();
    if (!trimmed) continue;
    const listed = BULLET_LINE.test(trimmed) || ORDERED_LINE.test(trimmed);
    const item = bareItem(trimmed);
    if (!item) continue;
    if (listed) {
      items.push(item);
      continue;
    }
    const parts = sentences(item);
    if (discussed) items.push(parts[0]);
    else items.push(...parts);
  }
  return items;
}

/** The text under one heading, as markdown the shared renderer can draw. */
function sectionMarkdown(block: BriefBlock): string {
  return listMarkdown(briefItems(block.heading, block.text).join("\n"), false);
}

/** The brief tab: one markdown document, headings included. */
export function briefMarkdown(brief: string | null, actionPoints: string[]): string {
  return briefBlocks(brief, actionPoints)
    .map((block) => {
      const body = sectionMarkdown(block);
      if (!body) return "";
      return `## ${block.heading}\n\n${body}`;
    })
    .filter(Boolean)
    .join("\n\n");
}

export function summaryMarkdown(summary: string | null): string {
  return summary?.trim() ?? "";
}

/** Action points as a numbered list. The renderer draws the numbers. */
export function actionMarkdown(points: string[]): string {
  return listMarkdown(points.join("\n"), true);
}

/** The markdown the open write-up tab shows. The transcript is not markdown. */
export function tabMarkdown(
  tab: CallTab,
  brief: string | null,
  summary: string | null,
  actionPoints: string[],
): string {
  if (tab === "brief") return briefMarkdown(brief, actionPoints);
  if (tab === "summary") return summaryMarkdown(summary);
  if (tab === "action_points") return actionMarkdown(actionPoints);
  return "";
}

/** The write-up with the marks taken off, so a plain paste matches the screen. */
function plainMarkdown(source: string): string {
  return source
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^#{1,6}[ \t]+/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Plain text of the tab that is open. */
export function copyTabText(
  tab: CallTab,
  brief: string | null,
  summary: string | null,
  actionPoints: string[],
  lines: CallLine[],
): string {
  if (tab === "transcript") return transcriptText(lines);
  return plainMarkdown(tabMarkdown(tab, brief, summary, actionPoints));
}

/**
 * The same tab as HTML. Write-up tabs go through the shared markdown renderer,
 * so a paste is the HTML on screen. The transcript is speaker lines.
 */
export function copyTabHtml(
  tab: CallTab,
  brief: string | null,
  summary: string | null,
  actionPoints: string[],
  lines: CallLine[],
  renderMarkdown: (markdown: string) => string,
): string {
  if (tab === "transcript") {
    return lines
      .map(
        (line) =>
          `<p><strong>${escapeHtml(line.speaker)}</strong>: ${escapeHtml(line.sentence)}</p>`,
      )
      .join("");
  }
  return renderMarkdown(tabMarkdown(tab, brief, summary, actionPoints));
}

/**
 * The few substantial subjects. A stretch under 8% of the call is left out.
 * At most five remain, the longest ones, drawn back in time order. When
 * nothing is that long, the single longest stretch is the core.
 */
export function coreTopics(topics: TimeSpan[], duration: number): TimeSpan[] {
  if (topics.length === 0) return [];
  const span = Math.max(duration, ...topics.map((topic) => topic.end), 1);
  const floor = span * CORE_TOPIC_SHARE;
  const substantial = topics.filter((topic) => topic.end - topic.start >= floor);
  const pool =
    substantial.length > 0
      ? substantial
      : [...topics].sort((a, b) => b.end - b.start - (a.end - a.start)).slice(0, 1);
  return [...pool]
    .sort((a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start)
    .slice(0, CORE_TOPIC_LIMIT)
    .sort((a, b) => a.start - b.start);
}

/**
 * Side-by-side widths. Past either stop the pointer has to travel a little
 * further, and then that column takes the whole row.
 */
export const TEXT_MIN_PX = 360;
export const MEDIA_MIN_PX = 400;
export const OVERTAKE_PX = 48;
export const SPLIT_HALF = 50;
export const SPLIT_STEP = 4;
/** Opening write-up share of the row. The recording gets the rest. */
export const SPLIT_OPEN = 46;

export type SplitLayout = "split" | "text" | "media";

/**
 * A meeting row keeps its title and its attendee, date and time on one line
 * while that line still fits beside the menu. `titleGap` sits between the
 * title and the details; `menuGap` sits before the menu.
 */
export function meetingRowFits(
  title: number,
  details: number,
  menu: number,
  titleGap: number,
  menuGap: number,
  available: number,
): boolean {
  if (!(available > 0)) return true;
  let needed = title;
  if (details > 0) needed += titleGap + details;
  if (menu > 0) needed += menuGap + menu;
  return needed <= available + 1;
}

/**
 * Stack the call header once the title would wrap beside the menu, or the
 * attendee, date and time would wrap on their own line. Each room is the
 * width that line has before it wraps.
 */
export function callHeadStacks(
  title: number,
  titleRoom: number,
  facts: number,
  factsRoom: number,
): boolean {
  if (titleRoom > 0 && title > titleRoom + 1) return true;
  if (factsRoom > 0 && facts > factsRoom + 1) return true;
  return false;
}

/** Tab labels stay on one line when their widths and the gaps between them fit. */
export function tabsFit(widths: number[], gap: number, available: number): boolean {
  if (!(available > 0) || widths.length === 0) return true;
  const needed = widths.reduce((sum, width) => sum + width, 0) + gap * (widths.length - 1);
  return needed <= available + 1;
}

/**
 * The play button stays centred, so each side is given the same room.
 * Speed and fullscreen still fit while the wider side has that room.
 */
export function controlsFit(
  left: number,
  right: number,
  play: number,
  gap: number,
  padding: number,
  available: number,
): boolean {
  if (!(available > 0)) return true;
  const needed = Math.max(left, right) * 2 + play + gap * 2 + padding;
  return needed <= available + 1;
}

/**
 * Volume stays beside play while the right-hand cluster fits in its column.
 * Once it would draw past the box, it moves into the dot menu.
 */
export function volumeFits(
  right: number,
  play: number,
  gap: number,
  padding: number,
  available: number,
): boolean {
  if (!(available > 0)) return true;
  const side = (available - play - gap * 2 - padding) / 2;
  return right <= side + 1;
}

/** Where the write-up sits after a drag asks for `textPx` of a `bodyPx` row. */
export function placeSplit(textPx: number, bodyPx: number): { layout: SplitLayout; share: number } {
  if (!(bodyPx > 0)) return { layout: "split", share: SPLIT_HALF };
  const half = bodyPx / 2;
  const minText = Math.min(TEXT_MIN_PX, half);
  const maxText = Math.max(bodyPx - MEDIA_MIN_PX, half);
  if (textPx <= minText - OVERTAKE_PX) return { layout: "media", share: SPLIT_HALF };
  if (textPx >= maxText + OVERTAKE_PX) return { layout: "text", share: SPLIT_HALF };
  const clamped = Math.min(maxText, Math.max(minText, textPx));
  return { layout: "split", share: Math.round((clamped / bodyPx) * 100) };
}

/** "Angie Magro 2m" for each person who shared, in first-seen order. */
export function shareLegend(shares: TimeSpan[]): string[] {
  const totals = new Map<string, number>();
  for (const share of shares) {
    totals.set(share.label, (totals.get(share.label) ?? 0) + Math.max(0, share.end - share.start));
  }
  return [...totals].map(([label, seconds]) => `${label} ${formatLength(seconds)}`);
}
